import Modal from 'react-modal';
import { useEffect, useState } from 'react';
import axios from 'axios';
import Button from '../Button';

export interface SaleItem {
    name: string;
    price: number;
}

const QUANTITY_ITEMS = new Set(['red bull', 'monster', 'water']);

const KioskSaleModal = ({
    item,
    isOpen,
    setIsOpen,
}: {
    item: SaleItem | null;
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
}) => {
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [qty, setQty] = useState(1);

    const supportsQuantity = item
        ? QUANTITY_ITEMS.has(item.name.trim().toLowerCase())
        : false;

    useEffect(() => {
        if (isOpen) {
            setQty(1);
            setDone(false);
            setError(null);
        }
    }, [isOpen, item]);

    const close = () => {
        setBusy(false);
        setDone(false);
        setError(null);
        setIsOpen(false);
    };

    const confirm = async () => {
        if (!item) return;
        const quantity = supportsQuantity ? Math.max(1, qty) : 1;
        setBusy(true);
        setError(null);
        try {
            await axios.post('/api/transactions', {
                guestID: 'SALE',
                firstName: 'SALE',
                lastName: item.name,
                stageName: item.name,
                fee: item.name,
                type: 'limited',
                value: item.price * quantity,
                points: 0,
                paymentMethod: 'cash',
                quantity,
            });
            setDone(true);
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Sale failed.');
        } finally {
            setBusy(false);
        }
    };

    const lineTotal = item ? item.price * (supportsQuantity ? qty : 1) : 0;

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={close}
            ariaHideApp={false}
            className="modal"
        >
            <header className="modal__header">
                <h1 className="modal__header--h1">
                    {item?.name}
                    {supportsQuantity && qty > 1 ? ` × ${qty}` : ''}{' '}
                    <span className="green-span">
                        ${lineTotal.toFixed(2)}
                    </span>
                </h1>
                {supportsQuantity && qty > 1 && (
                    <h3
                        className="transaction-container__text"
                        style={{ marginTop: '0.5rem' }}
                    >
                        ({qty} × ${item?.price.toFixed(2)})
                    </h3>
                )}
            </header>

            {!done && (
                <>
                    {supportsQuantity && (
                        <>
                            <h3 className="transaction-container__text">
                                Quantity:{' '}
                                <span className="green-span">{qty}</span>
                            </h3>
                            <div>
                                <Button
                                    color={'red'}
                                    margin={true}
                                    onClick={() =>
                                        setQty((q) => Math.max(1, q - 1))
                                    }
                                >
                                    −
                                </Button>
                                <Button
                                    color={'green'}
                                    margin={true}
                                    onClick={() => setQty((q) => q + 1)}
                                >
                                    +
                                </Button>
                            </div>
                        </>
                    )}

                    {error && (
                        <h3 className="transaction-container__text">
                            <span className="red-span">{error}</span>
                        </h3>
                    )}
                    <Button color={'green'} margin={true} onClick={confirm}>
                        {busy
                            ? 'Recording…'
                            : `Confirm Sale — $${lineTotal.toFixed(2)}`}
                    </Button>
                    <Button color={'red'} margin={true} onClick={close}>
                        Cancel
                    </Button>
                </>
            )}

            {done && (
                <>
                    <h1 className="modal__header--h1">
                        <span className="green-span">Sale recorded</span>
                    </h1>
                    <Button color={'green'} margin={true} onClick={close}>
                        Done
                    </Button>
                </>
            )}
        </Modal>
    );
};

export default KioskSaleModal;
